(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 769px)');

  function getHeaderOffset() {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return 0;
    return Math.round(stickyHeader.getBoundingClientRect().height);
  }

  function applyHeaderOffset(section) {
    section.style.setProperty('--spc-header-offset', `${getHeaderOffset()}px`);
  }

  function getStickyHeaderGap(section) {
    return Number.parseFloat(section?.dataset?.stickyHeaderGap) || 0;
  }

  function isMobileScrollEnabled(section) {
    return section?.dataset?.mobileScrollEnabled === 'true';
  }

  function getMobileScrollStep(section) {
    return Number.parseFloat(section?.dataset?.mobileScrollStep) || 120;
  }

  function getMobileSwitchDelay(section) {
    const mobileScrollStep = getMobileScrollStep(section);
    return Math.max(160, Math.min(420, mobileScrollStep * 2));
  }

  function getPinOffset(section) {
    const desktopGap = DESKTOP_MQ.matches ? getStickyHeaderGap(section) : 0;
    return getHeaderOffset() + desktopGap;
  }

  function destroyScrollProductCarousel(section) {
    if (!section) return;

    section._spcTriggers?.forEach((trigger) => trigger.kill());
    section._spcTriggers = [];

    section._spcNavHandlers?.forEach(({ button, handler }) => {
      button.removeEventListener('click', handler);
    });
    section._spcNavHandlers = [];

    if (section._spcNavReleaseTimer) {
      window.clearTimeout(section._spcNavReleaseTimer);
      section._spcNavReleaseTimer = null;
    }

    section._spcResizeObserver?.disconnect();
    section._spcResizeObserver = null;

    section._spcHeaderObserver?.disconnect();
    section._spcHeaderObserver = null;

    section._spcMediaHandler?.();
    section._spcMediaHandler = null;

    section.style.removeProperty('--spc-header-offset');
    section.querySelector('.spc-pin-panel')?.style.removeProperty('top');
  }

  function scrollToSlide(scrollTrigger, slideIndex, totalSlides, onComplete) {
    if (!scrollTrigger || totalSlides <= 1) {
      onComplete?.();
      return;
    }

    const progress = slideIndex / (totalSlides - 1);
    const targetScroll = scrollTrigger.start + progress * (scrollTrigger.end - scrollTrigger.start);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const finish = () => onComplete?.();

    if (reducedMotion) {
      window.scrollTo(0, targetScroll);
      finish();
      return;
    }

    window.scrollTo({
      top: targetScroll,
      behavior: 'smooth',
    });

    const startedAt = performance.now();
    const maxWait = 2000;
    let stableFrames = 0;
    let lastScrollY = window.scrollY;

    const poll = () => {
      const currentScrollY = window.scrollY;
      const reachedTarget = Math.abs(currentScrollY - targetScroll) < 2;
      const scrollSettled = Math.abs(currentScrollY - lastScrollY) < 0.5;
      const timedOut = performance.now() - startedAt > maxWait;

      stableFrames = reachedTarget && scrollSettled ? stableFrames + 1 : 0;
      lastScrollY = currentScrollY;

      if (stableFrames >= 3 || timedOut) {
        finish();
        return;
      }

      requestAnimationFrame(poll);
    };

    requestAnimationFrame(poll);
  }

  function initScrollProductCarousel(root) {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(() => initScrollProductCarousel(root), 100);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const sections =
      root && root.classList && root.classList.contains('spc-section')
        ? [root]
        : Array.from((root && root.querySelectorAll ? root : document).querySelectorAll('.spc-section'));

    sections.forEach((section) => {
      destroyScrollProductCarousel(section);

      const desktop = section.querySelector('.spc-desktop');
      const pinPanel = section.querySelector('.spc-pin-panel');
      const scrollTrack = section.querySelector('.spc-scroll-track');
      const infos = section.querySelectorAll('.spc-info');
      const products = section.querySelectorAll('.spc-product');
      const navItems = section.querySelectorAll('.spc-nav-item');
      const totalSlides = infos.length;

      if (totalSlides === 0 || !desktop || !pinPanel) return;

      section._spcTriggers = [];
      section._spcNavHandlers = [];
      let activeIndex = 0;
      let pinTrigger = null;
      let suppressScrollSync = false;
      let navScrollId = 0;
      let navTargetIndex = null;
      let lastMobileSlideSwitchAt = 0;

      function setActiveSlide(index) {
        const nextIndex = Math.max(0, Math.min(totalSlides - 1, index));
        if (nextIndex === activeIndex && section.dataset.spcInitialized === 'true') return;

        activeIndex = nextIndex;

        infos.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        products.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        navItems.forEach((el, i) => {
          el.classList.toggle('active', i === activeIndex);
        });

        if (!DESKTOP_MQ.matches && isMobileScrollEnabled(section)) {
          navItems[activeIndex]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      }

      function releaseNavScrollSync(scrollId, targetIndex) {
        if (scrollId !== navScrollId) return;

        if (section._spcNavReleaseTimer) {
          window.clearTimeout(section._spcNavReleaseTimer);
        }

        section._spcNavReleaseTimer = window.setTimeout(() => {
          if (scrollId !== navScrollId) return;

          setActiveSlide(targetIndex);
          navTargetIndex = null;
          suppressScrollSync = false;
          section._spcNavReleaseTimer = null;
        }, 120);
      }

      function setPinTop() {
        pinPanel.style.top = `${getPinOffset(section)}px`;
      }

      function enforcePinOffset(self) {
        const isActive = self?.isActive ?? pinTrigger?.isActive;
        if (!isActive) return;
        setPinTop();
      }

      function setupDesktopPin() {
        if (!DESKTOP_MQ.matches) return;

        applyHeaderOffset(section);
        setActiveSlide(0);

        if (totalSlides <= 1 || !scrollTrack) return;

        pinTrigger = ScrollTrigger.create({
          trigger: desktop,
          start: () => `top top+=${getPinOffset(section)}`,
          end: 'bottom bottom',
          pin: pinPanel,
          pinSpacing: false,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          snap: {
            snapTo: 1 / (totalSlides - 1),
            duration: { min: 0.15, max: 0.45 },
            delay: 0.05,
            ease: 'power1.inOut',
          },
          onUpdate(self) {
            if (suppressScrollSync) {
              if (navTargetIndex !== null) setActiveSlide(navTargetIndex);
              return;
            }

            const index = Math.round(self.progress * (totalSlides - 1));
            setActiveSlide(index);
          },
          onEnter: setPinTop,
          onEnterBack: setPinTop,
          onRefresh: enforcePinOffset,
        });

        section._spcTriggers.push(pinTrigger);
      }

      function setupMobilePin() {
        if (DESKTOP_MQ.matches || !isMobileScrollEnabled(section)) return;

        applyHeaderOffset(section);
        setActiveSlide(0);

        if (totalSlides <= 1 || !scrollTrack) return;

        const getNextMobileSnapPoint = (progress) => {
          const targetIndex = Math.round(progress * (totalSlides - 1));
          if (targetIndex === activeIndex) return activeIndex / (totalSlides - 1);

          const direction = targetIndex > activeIndex ? 1 : -1;
          const nextIndex = Math.max(0, Math.min(totalSlides - 1, activeIndex + direction));
          return nextIndex / (totalSlides - 1);
        };

        pinTrigger = ScrollTrigger.create({
          trigger: desktop,
          start: () => `top top+=${getPinOffset(section)}`,
          end: 'bottom bottom',
          pin: pinPanel,
          pinSpacing: false,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          snap: {
            snapTo: getNextMobileSnapPoint,
            duration: { min: 0.15, max: 0.4 },
            delay: 0.04,
            ease: 'power1.inOut',
          },
          onUpdate(self) {
            if (suppressScrollSync) {
              if (navTargetIndex !== null) setActiveSlide(navTargetIndex);
              return;
            }

            const targetIndex = Math.round(self.progress * (totalSlides - 1));
            if (targetIndex === activeIndex) return;

            const now = performance.now();
            if (now - lastMobileSlideSwitchAt < getMobileSwitchDelay(section)) return;

            const direction = targetIndex > activeIndex ? 1 : -1;
            lastMobileSlideSwitchAt = now;
            setActiveSlide(activeIndex + direction);
          },
          onEnter: setPinTop,
          onEnterBack: setPinTop,
          onRefresh: enforcePinOffset,
        });

        section._spcTriggers.push(pinTrigger);
      }

      navItems.forEach((button, index) => {
        const handler = () => {
          const scrollId = ++navScrollId;
          suppressScrollSync = true;
          navTargetIndex = index;
          setActiveSlide(index);
          scrollToSlide(pinTrigger, index, totalSlides, () => releaseNavScrollSync(scrollId, index));
        };

        button.addEventListener('click', handler);
        section._spcNavHandlers.push({ button, handler });
      });

      setupDesktopPin();
      setupMobilePin();
      section.dataset.spcInitialized = 'true';

      const images = section.querySelectorAll('img');
      images.forEach((img) => {
        if (img.complete) {
          ScrollTrigger.refresh();
        } else {
          img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
        }
      });

      if (window.ResizeObserver) {
        section._spcResizeObserver = new ResizeObserver(() => {
          applyHeaderOffset(section);
          ScrollTrigger.refresh();
          enforcePinOffset(pinTrigger);
        });
        section._spcResizeObserver.observe(section);

        const stickyHeader = document.querySelector('sticky-header');
        if (stickyHeader) {
          section._spcHeaderObserver = new ResizeObserver(() => {
            applyHeaderOffset(section);
            ScrollTrigger.refresh();
            enforcePinOffset(pinTrigger);
          });
          section._spcHeaderObserver.observe(stickyHeader);
        }
      }

      const handleMediaChange = () => {
        const container = section.closest('.shopify-section') || document;
        destroyScrollProductCarousel(section);
        initScrollProductCarousel(container);
      };

      if (DESKTOP_MQ.addEventListener) {
        DESKTOP_MQ.addEventListener('change', handleMediaChange);
      } else {
        DESKTOP_MQ.addListener(handleMediaChange);
      }

      section._spcMediaHandler = () => {
        if (DESKTOP_MQ.removeEventListener) {
          DESKTOP_MQ.removeEventListener('change', handleMediaChange);
        } else {
          DESKTOP_MQ.removeListener(handleMediaChange);
        }
      };
    });

    ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScrollProductCarousel(document));
  } else {
    initScrollProductCarousel(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector('.spc-section')) {
      initScrollProductCarousel(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.spc-section').forEach(destroyScrollProductCarousel);
  });
})();
